/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreObjects — server-safe base classes for typed data objects and collections.
 *
 * Exports:
 *   kObjectCore      — base data object with JSON serialisation and deep-clone
 *   kObjectArrayCore — typed collection with sort, lookup, and async iteration
 *
 * No browser APIs. Safe to import in Node.js, Azure Functions, or the browser.
 */

import {
    getCoreUtils, nz, nb, n0, kNotNullOrUndefined, kNotEmptyString, getRandomInt, doCallBack,
} from "./kCoreHandler";
import { kTimeCore } from "./kCoreTime";

// ── kObjectCore ───────────────────────────────────────────────────────────────

/**
 * Base class for all typed data objects.
 *
 * Provides:
 *   - Standard identity fields: Title, ID, Index, UniqueID
 *   - ToJSONObj / FromSaved for round-trip JSON serialisation
 *   - CloneObject for deep-copying into a subclass
 */
export class kObjectCore {
    public Title = "";
    public ID = 0;
    public Index = 0;
    public UniqueID = 0;

    // Stub methods — browser kObject overrides these with real DOM implementations.
    // Present here so the type system treats kObjectCore and kObject as compatible.
    public ShowProgress = (ProgressMessage: string, Done: number, Total: number): void => { };
    public ToRow = (): any => { return null; };
    public FromRow = (CurrRow: any): void => { };
    public ToAnchor = (): any => { return null; };

    public constructor(Title?: string) {
        this.Title = nz(Title);
        this.UniqueID = new Date().getTime() + Math.floor(Math.random() * 1000000);
    }

    /**
     * Serialise to a plain, JSON-safe object.
     * kTime fields are stored as ISO strings ("yyyy-MM-dd HH:mm").
     * Functions and complex objects are omitted.
     */
    public ToJSONObj = (): any => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            const Saved: any = {};
            const Self = this as any;
            for (const Property in this) {
                if (typeof Self[Property] === "function") { continue; }
                let PropConstructorName: string = "";
                if (Self[Property] !== null && Self[Property] !== undefined) {
                    if (Self[Property].constructor !== null && Self[Property].constructor !== undefined) {
                        PropConstructorName = Self[Property].constructor.name;
                    }
                }
                if (typeof Self[Property] === "object"
                    && PropConstructorName === "kTime") {
                    Saved[Property] = (Self[Property] as kTimeCore).ToISODateTime();
                } else if (typeof Self[Property] !== "object") {
                    Saved[Property] = Self[Property];
                }
            }
            return Saved;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObject.ToJSONObj"); return; }
    };

    /**
     * Restore fields from a plain object previously produced by ToJSONObj.
     * kTime fields are detected by constructor name and parsed from their ISO string.
     */
    public FromSaved = (JSONObj: any): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            const Self = this as any;
            for (const Property in JSONObj) {
                let ClassName = "";
                try {
                    if (Self[Property] !== null && Self[Property] !== undefined && Self[Property].constructor) {
                        ClassName = Self[Property].constructor.name;
                    }
                } catch (e) { /* field may not exist yet on this instance */ }
                if (ClassName === "kTime") {
                    (Self[Property] as kTimeCore).FromISODateTime(nz(JSONObj[Property]));
                } else {
                    Self[Property] = JSONObj[Property];
                }
            }
            // backwards compatibility: older saved objects used "Name" instead of "Title"
            if (this.Title === undefined) { this.Title = JSONObj.Name; }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObject.FromSaved"); }
    };

    /**
     * Deep-clone this instance into a new object of type NewClass.
     * NewClass must extend kObjectCore and accept an optional title string in its constructor.
     */
    public CloneObject = (NewClass: any): any => {
        if (getCoreUtils().kErrorMode) { return null; }
        try {
            const NewObj: kObjectCore = new NewClass(this.Title);
            NewObj.FromSaved(this.ToJSONObj());
            return NewObj;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObject.CloneObject"); return null; }
    };
}

// ── kObjectArrayCore ─────────────────────────────────────────────────────────

/**
 * Typed collection of kObjectCore instances.
 *
 * Provides:
 *   - CRUD: push, Remove*, GetItemBy*
 *   - Sort: SortByTitle, SortByProperty, SortAndRemoveDoubles
 *   - Serialisation: ToPersistedArray / FromPersistedArray, ToTitlesArray / FromTitlesArray
 *   - Async iteration: CallFunctionOnEach (small setTimeout between calls to prevent stack overflow)
 */
export class kObjectArrayCore {
    public Items: kObjectCore[] = [];
    /** Constructor used by GetOrAddByTitle, FromPersistedArray, and FromTitlesArray. */
    public NewItem: new (title?: string) => kObjectCore = kObjectCore;
    // eslint-disable-next-line @typescript-eslint/ban-types
    public ExtendedClass: Function | null = null;

    protected _ActionCounter = 0;
    protected _FunctionName: string = "";
    protected _ObjectName: string = "";
    // eslint-disable-next-line @typescript-eslint/ban-types
    protected _ActionDoneCallback: Function | null = null;
    protected _ProgressMessage = "";
    protected _ProgressCB: any = null;
    protected _ForEachPar: any = null;
    protected _SortPropName = "";
    protected _SortPropNumeric = false;

    // ── Collection management ─────────────────────────────────────────────────

    /** Append an item and update its Index field. */
    public push = (NewObject: kObjectCore): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            if (kNotNullOrUndefined(NewObject)) {
                NewObject.Index = this.Items.length;
                this.Items.push(NewObject);
            }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.push"); }
    };

    /** Remove the item at the given array index. */
    public RemoveItemByIndex = (Index: number): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this.Items.splice(Index, 1);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.RemoveItemByIndex"); }
    };

    /** Remove the first item whose Title matches. */
    public RemoveItemByTitle = (Title: string): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            const Item: kObjectCore = this.GetItemByTitle(Title);
            if (kNotNullOrUndefined(Item)) { this.RemoveItemByIndex(Item.Index); }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.RemoveItemByTitle"); }
    };

    /** Remove the item with the given UniqueID. */
    public RemoveItemByUniqueID = (UniqueID: number): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            const Item: kObjectCore = this.GetItemByUniqueID(UniqueID);
            if (kNotNullOrUndefined(Item)) { this.RemoveItemByIndex(Item.Index); }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.RemoveItemByUniqueID"); }
    };

    /** Remove the item with the given ID. */
    public RemoveItemByID = (ID: number): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            const Item: kObjectCore = this.GetItemByID(ID);
            if (kNotNullOrUndefined(Item)) { this.RemoveItemByIndex(Item.Index); }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.RemoveItemByID"); }
    };

    // ── Lookup ────────────────────────────────────────────────────────────────

    /** Find by Title. Updates Index on all items. Returns null when not found. */
    public GetItemByTitle = (Title: string): any => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            let Found: kObjectCore | null = null;
            let Index = 0;
            for (const Item of this.Items) {
                if (Item.Title === Title) { Found = Item; }
                Item.Index = Index++;
            }
            return Found;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.GetItemByTitle"); }
    };

    /** Return the item with the given Title, or create and push a new one. */
    public GetOrAddByTitle = (Title: string): kObjectCore => {
        if (getCoreUtils().kErrorMode) { return new kObjectCore(); }
        try {
            const Existing: kObjectCore | null = this.GetItemByTitle(Title);
            if (Existing === null) {
                const New: kObjectCore = new this.NewItem();
                New.Title = Title;
                this.push(New);
                return New;
            }
            return Existing;
        } catch (e) {
            getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.GetOrAddByTitle");
            return new kObjectCore();
        }
    };

    /** Find by UniqueID. Updates Index on all items. */
    public GetItemByUniqueID = (UniqueID: number): any => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            let Found: kObjectCore | null = null;
            let Index = 0;
            for (const Item of this.Items) {
                if (Item.UniqueID === UniqueID) { Found = Item; }
                Item.Index = Index++;
            }
            return Found;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.GetItemByUniqueID"); }
    };

    /** Find by ID. Updates Index on all items. */
    public GetItemByID = (ID: number): any => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            let Found: kObjectCore | null = null;
            let Index = 0;
            for (const Item of this.Items) {
                if (Item.ID === ID) { Found = Item; }
                Item.Index = Index++;
            }
            return Found;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.GetItemByID"); }
    };

    /** Return a random item from the collection. */
    public GetRandomItem = (): kObjectCore => {
        if (getCoreUtils().kErrorMode) { return new kObjectCore(); }
        try {
            return this.Items[getRandomInt(0, this.Items.length - 1)];
        } catch (e) {
            getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.GetRandomItem");
            return new kObjectCore();
        }
    };

    // ── Sorting ───────────────────────────────────────────────────────────────

    /** Sort items alphabetically by Title (case-insensitive). */
    public SortByTitle = (): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this.Items.sort(this._SortByTitleFn);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.SortByTitle"); }
    };

    protected _SortByTitleFn = (A: kObjectCore, B: kObjectCore): number => {
        if (A.Title.toLowerCase() < B.Title.toLowerCase()) { return -1; }
        if (A.Title.toLowerCase() > B.Title.toLowerCase()) { return 1; }
        return 0;
    };

    /**
     * Sort by any property.
     * @param Numeric  When true, values are compared numerically rather than as strings.
     */
    public SortByProperty = (Property: string, Ascending = true, Numeric?: boolean): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this._SortPropName = Property;
            this._SortPropNumeric = nb(Numeric);
            this.Items.sort(Ascending ? this._SortPropAscFn : this._SortPropDescFn);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.SortByProperty"); }
    };

    protected _SortPropAscFn = (A: any, B: any): number => {
        if (!kNotNullOrUndefined(A[this._SortPropName])
            || !kNotNullOrUndefined(B[this._SortPropName])) {
            return 0;
        }
        if (this._SortPropNumeric) {
            return n0(A[this._SortPropName]) - n0(B[this._SortPropName]);
        }
        const AVal: string = nz(A[this._SortPropName]);
        const BVal: string = nz(B[this._SortPropName]);
        return AVal < BVal ? -1 : AVal > BVal ? 1 : 0;
    };

    protected _SortPropDescFn = (A: any, B: any): number => -this._SortPropAscFn(A, B);

    /** Sort by Title then remove items with duplicate titles. */
    public SortAndRemoveDoubles = (): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this.SortByTitle();
            this.RemoveTitleDoubles();
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.SortAndRemoveDoubles"); }
    };

    /** Remove items whose Title already appeared earlier in the array. */
    public RemoveTitleDoubles = (): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            const Seen: string[] = [];
            const Unique: kObjectCore[] = [];
            for (const Item of this.Items) {
                if (kNotNullOrUndefined(Item.Title) && Seen.indexOf(nz(Item.Title)) === -1) {
                    Item.Index = Unique.length;
                    Unique.push(Item);
                    Seen.push(nz(Item.Title));
                }
            }
            this.Items = Unique;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.RemoveTitleDoubles"); }
    };

    // ── Serialisation ─────────────────────────────────────────────────────────

    /** Return an array of Title strings, optionally sorted. */
    public ToTitlesArray = (Sort?: boolean): string[] => {
        if (getCoreUtils().kErrorMode) { return []; }
        try {
            const Titles: string[] = [];
            let Index = 0;
            for (const Item of this.Items) {
                Titles.push(nz(Item.Title));
                Item.Index = Index++;
            }
            return Sort === true ? Titles.sort() : Titles;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.ToTitlesArray"); return []; }
    };

    /** Serialise all items to plain JSON-safe objects (via kObject.ToJSONObj). */
    public ToPersistedArray = (): any[] => {
        if (getCoreUtils().kErrorMode) { return []; }
        try {
            return this.Items.map((Item: kObjectCore) => Item.ToJSONObj());
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.ToPersistedArray"); return []; }
    };

    /** Restore items from a plain array previously produced by ToPersistedArray. */
    public FromPersistedArray = (PersistedArray: any[]): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this.Items = [];
            if (PersistedArray != null && PersistedArray.length > 0) {
                for (const Obj of PersistedArray) {
                    const Item: kObjectCore = new this.NewItem();
                    Item.FromSaved(Obj);
                    this.push(Item);
                }
            }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.FromPersistedArray"); }
    };

    /** Create an item for each string in Titles. Pass Empty=true to clear first. */
    public FromTitlesArray = (Titles: string[], Empty?: boolean): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            if (Empty) { this.Items = []; }
            for (const Title of Titles) {
                const New: any = new this.NewItem();
                New.Index = this.Items.length;
                New.Title = Title;
                this.push(New);
            }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.FromTitlesArray"); }
    };

    // ── Async iteration ───────────────────────────────────────────────────────

    /**
     * Call FunctionName on each item sequentially.
     * A 5 ms setTimeout between calls prevents stack overflows on large arrays
     * and keeps the event loop responsive.
     *
     * @param FunctionName   Method name (string) to call on each item.
     * @param AllDone        Callback invoked after the last item.
     * @param ObjectName     Optional sub-object name on each item (calls item[ObjectName][FunctionName]).
     * @param StartIndex     Resume from this index (default 0).
     * @param ProgressMessage  Passed to the ProgressCallback on each step.
     * @param ProgressCallback Optional callback(message, done, total) for progress reporting.
     * @param Parameter      Forwarded as the first argument to each method call.
     */
    public CallFunctionOnEach = (
        FunctionName: any,
        AllDone: any,
        ObjectName?: any,
        StartIndex?: number,
        ProgressMessage?: string,
        ProgressCallback?: any,
        Parameter?: any
    ): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this._ObjectName = nz(ObjectName);
            this._FunctionName = FunctionName;
            this._ActionDoneCallback = AllDone;
            this._ForEachPar = Parameter;
            if (ProgressCallback) { this._ProgressCB = ProgressCallback; }
            this._ActionCounter = kNotNullOrUndefined(StartIndex) ? n0(StartIndex) : 0;
            this._ProgressMessage = nz(ProgressMessage);
            this._NextCallback();
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray.CallFunctionOnEach"); }
    };

    protected _NextCallback = (): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            if (this._ActionCounter < this.Items.length) {
                this._ActionCounter++;
                if (this._ProgressCB) {
                    this._ProgressCB(this._ProgressMessage, this._ActionCounter, this.Items.length);
                }
                const CurrentItem = this.Items[this._ActionCounter - 1] as any;
                const NextCb = this._NextCallback;
                setTimeout(() => {
                    if (kNotEmptyString(this._ObjectName)) {
                        CurrentItem[this._ObjectName][this._FunctionName](this._ForEachPar, NextCb);
                    } else {
                        CurrentItem[this._FunctionName](this._ForEachPar, NextCb);
                    }
                }, 5);
            } else {
                doCallBack(this._ActionDoneCallback);
            }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kObjectArray._NextCallback"); }
    };
}
